import { useEffect, useRef, useState, useCallback } from "react";
import { MapPin, Loader2 } from "lucide-react";

// Debounce helper
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

const AddressAutocomplete = ({ 
  value, 
  onChange, 
  onPlaceSelect,
  placeholder = "Entrez une adresse...",
  className = "",
  disabled = false,
  "data-testid": dataTestId
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  // Sync external value
  useEffect(() => {
    if (value !== undefined && value !== inputValue) {
      setInputValue(value);
    }
  }, [value]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch suggestions from French API
  const fetchSuggestions = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 3) {
        setSuggestions([]);
        setShowSuggestions(false);
        return;
      }

      setIsLoading(true);
      try {
        const response = await fetch(
          `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5&autocomplete=1`
        );
        
        if (!response.ok) throw new Error("API error");
        
        const data = await response.json();
        
        const formattedSuggestions = data.features?.map((feature) => ({
          label: feature.properties.label,
          address: feature.properties.name,
          city: feature.properties.city,
          postal_code: feature.properties.postcode,
          lat: feature.geometry.coordinates[1],
          lng: feature.geometry.coordinates[0],
          street: feature.properties.street,
          housenumber: feature.properties.housenumber
        })) || [];
        
        setSuggestions(formattedSuggestions);
        setShowSuggestions(formattedSuggestions.length > 0);
        setSelectedIndex(-1);
      } catch (error) {
        console.error("Error fetching address suggestions:", error);
        setSuggestions([]);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange?.(newValue);
    fetchSuggestions(newValue);
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion.label);
    onChange?.(suggestion.label);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Call onPlaceSelect with structured data
    onPlaceSelect?.({
      formatted_address: suggestion.label,
      address: suggestion.address,
      city: suggestion.city,
      postal_code: suggestion.postal_code,
      lat: suggestion.lat,
      lng: suggestion.lng,
      street: suggestion.street,
      street_number: suggestion.housenumber,
      country: "France"
    });
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) => 
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        break;
      default:
        break;
    }
  };

  const handleFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  return (
    <div ref={wrapperRef} className="relative address-autocomplete-wrapper">
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        className={`flex h-9 w-full rounded-md border px-3 py-1 text-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 input-dark pr-10 ${className}`}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={dataTestId}
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck="false"
      />
      
      {isLoading ? (
        <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748B] animate-spin pointer-events-none" />
      ) : (
        <MapPin className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#64748B] pointer-events-none" />
      )}

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <ul 
          className="absolute z-50 w-full mt-1 bg-[#1F2A38] border border-[#2A3A4D] rounded-md shadow-lg max-h-60 overflow-auto"
          data-testid="address-suggestions"
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
              className={`px-3 py-2 cursor-pointer text-sm transition-colors ${
                index === selectedIndex 
                  ? "bg-[#00E073]/20 text-white" 
                  : "text-[#94A3B8] hover:bg-[#2A3A4D] hover:text-white"
              }`}
              data-testid={`suggestion-${index}`}
            >
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-[#00E073]" />
                <div>
                  <p className="font-medium text-white">{suggestion.address}</p>
                  <p className="text-xs text-[#64748B]">
                    {suggestion.postal_code} {suggestion.city}
                  </p>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

// Geocoding utility function using French API
export const geocodeAddress = async (address) => {
  try {
    const response = await fetch(
      `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(address)}&limit=1`
    );
    
    if (!response.ok) throw new Error("API error");
    
    const data = await response.json();
    
    if (data.features && data.features.length > 0) {
      const feature = data.features[0];
      return {
        lat: feature.geometry.coordinates[1],
        lng: feature.geometry.coordinates[0],
        formatted_address: feature.properties.label,
        city: feature.properties.city,
        postal_code: feature.properties.postcode
      };
    }
    
    throw new Error("No results found");
  } catch (error) {
    throw error;
  }
};

// Get city from postal code using French API
export const getCityFromPostalCode = async (postalCode) => {
  if (!postalCode || postalCode.length !== 5) return null;
  
  try {
    const response = await fetch(
      `https://geo.api.gouv.fr/communes?codePostal=${postalCode}&fields=nom&limit=5`
    );
    
    if (!response.ok) throw new Error("API error");
    
    const data = await response.json();
    
    if (data && data.length > 0) {
      return {
        city: data[0].nom,
        cities: data.map(c => c.nom)
      };
    }
    
    return null;
  } catch (error) {
    console.error("Error fetching city from postal code:", error);
    return null;
  }
};

// Calculate distance between two points (in km)
export const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = 
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLng / 2) * Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export default AddressAutocomplete;
